import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaExternalLinkAlt, FaFilePdf, FaImage, FaVideo } from "react-icons/fa";
import fallbackThumbnail from "../assets/Sahabatkakao.png";
import "./BeritaKakao.css";

const DESCRIPTION_PREVIEW_LENGTH = 260;
const recordedNewsEvents = new Set();

function createUuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`.slice(0, 36);
}

function trackingAllowed() {
  return !(
    navigator.doNotTrack === "1" ||
    window.doNotTrack === "1" ||
    navigator.globalPrivacyControl === true
  );
}

function getTrackingIdentity() {
  let uid = localStorage.getItem("anon_uid");
  let sid = sessionStorage.getItem("sid");
  if (!uid) {
    uid = createUuid();
    localStorage.setItem("anon_uid", uid);
  }
  if (!sid) {
    sid = createUuid();
    sessionStorage.setItem("sid", sid);
  }
  sessionStorage.setItem("sid_ts", String(Date.now()));
  return { uid, sid };
}

function recordNewsEvent(apiUrl, eventType, item = null, dedupeKey = "", details = {}) {
  const hasNamedAccess = Boolean(details.visitor_name && details.institution);
  if (!trackingAllowed() && !hasNamedAccess) return Promise.resolve();

  const identity = trackingAllowed() ? getTrackingIdentity() : { uid: null, sid: null };
  const key = dedupeKey ? `${identity.sid}|${dedupeKey}` : "";
  if (key && recordedNewsEvents.has(key)) return Promise.resolve();
  if (key) recordedNewsEvents.add(key);

  return fetch(`${apiUrl}/analytics/news-event`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      event_type: eventType,
      news_id: item?.id || null,
      ...identity,
      ...details,
    }),
    keepalive: true,
  })
    .then(async (response) => {
      if (response.ok) return;
      const json = await response.json().catch(() => ({}));
      throw new Error(json?.message || `HTTP ${response.status}`);
    })
    .catch((error) => {
      if (key) recordedNewsEvents.delete(key);
      if (hasNamedAccess) throw error;
    });
}

function getApiUrl() {
  const raw = String(process.env.REACT_APP_API_URL || "/api").trim();
  if (!/^https?:\/\//i.test(raw) || typeof window === "undefined") return raw || "/api";

  try {
    const url = new URL(raw);
    const localApi = /^(localhost|127\.0\.0\.1)$/i.test(url.hostname);
    const localWeb = /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
    if (!localWeb && localApi) return `${window.location.origin}/api`;
    if (window.location.protocol === "https:" && url.protocol === "http:") {
      return raw.replace(/^http:/i, "https:");
    }
    return raw;
  } catch {
    return "/api";
  }
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function resolveThumbnailUrl(apiUrl, value) {
  const raw = String(value || "").trim();
  if (!raw) return fallbackThumbnail;

  const fileMatch = raw.match(/drive\.google\.com\/file\/d\/([^/]+)/i);
  if (fileMatch?.[1]) return `${apiUrl}/news/thumbnail/${encodeURIComponent(fileMatch[1])}`;

  try {
    const parsed = new URL(raw);
    if (parsed.hostname === "drive.google.com") {
      const id = parsed.searchParams.get("id");
      if (id) return `${apiUrl}/news/thumbnail/${encodeURIComponent(id)}`;
    }
  } catch {
    return fallbackThumbnail;
  }

  return raw;
}

export default function BeritaKakao() {
  const apiUrl = useMemo(getApiUrl, []);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedNews, setExpandedNews] = useState(() => new Set());
  const [mediaAccess, setMediaAccess] = useState(null);
  const [mediaAccessForm, setMediaAccessForm] = useState({ name: "", institution: "" });
  const [mediaAccessError, setMediaAccessError] = useState("");
  const [submittingMediaAccess, setSubmittingMediaAccess] = useState(false);

  const openMediaAccess = (item, eventType, url, label) => {
    const savedName = localStorage.getItem("news_visitor_name") || "";
    const savedInstitution = localStorage.getItem("news_visitor_institution") || "";

    if (savedName && savedInstitution) {
      const targetWindow = window.open("about:blank", "_blank");
      recordNewsEvent(apiUrl, eventType, item, "", {
        visitor_name: savedName,
        institution: savedInstitution,
      })
        .then(() => {
          if (targetWindow) {
            targetWindow.opener = null;
            targetWindow.location.replace(url);
          } else {
            window.location.assign(url);
          }
        })
        .catch((err) => {
          if (targetWindow) targetWindow.close();
          alert(err?.message || "Gagal mencatat akses media. Silakan coba kembali.");
        });
      return;
    }

    setMediaAccess({ item, eventType, url, label });
    setMediaAccessForm({
      name: savedName,
      institution: savedInstitution,
    });
    setMediaAccessError("");
  };

  const closeMediaAccess = () => {
    if (submittingMediaAccess) return;
    setMediaAccess(null);
    setMediaAccessError("");
  };

  const submitMediaAccess = async (event) => {
    event.preventDefault();
    if (!mediaAccess || submittingMediaAccess) return;

    const visitorName = mediaAccessForm.name.trim();
    const institution = mediaAccessForm.institution.trim();
    if (!visitorName || !institution) {
      setMediaAccessError("Nama dan Institusi wajib diisi.");
      return;
    }

    const targetWindow = window.open("about:blank", "_blank");
    setSubmittingMediaAccess(true);
    setMediaAccessError("");
    try {
      await recordNewsEvent(apiUrl, mediaAccess.eventType, mediaAccess.item, "", {
        visitor_name: visitorName,
        institution,
      });
      localStorage.setItem("news_visitor_name", visitorName);
      localStorage.setItem("news_visitor_institution", institution);

      if (targetWindow) {
        targetWindow.opener = null;
        targetWindow.location.replace(mediaAccess.url);
      } else {
        window.location.assign(mediaAccess.url);
      }
      setMediaAccess(null);
    } catch (err) {
      if (targetWindow) targetWindow.close();
      setMediaAccessError(err?.message || "Gagal mencatat akses media. Silakan coba kembali.");
    } finally {
      setSubmittingMediaAccess(false);
    }
  };

  const toggleDescription = (id) => {
    const item = news.find((entry) => entry.id === id);
    if (!expandedNews.has(id) && item) {
      recordNewsEvent(apiUrl, "expand_click", item);
    }
    setExpandedNews((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    let active = true;

    axios
      .get(`${apiUrl}/news/public`)
      .then((response) => {
        if (!active) return;
        setNews(Array.isArray(response?.data?.data) ? response.data.data : []);
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setNews([]);
        setError(err?.response?.data?.message || "Berita belum dapat dimuat.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [apiUrl]);

  useEffect(() => {
    recordNewsEvent(apiUrl, "page_open", null, "page_open");
  }, [apiUrl]);

  useEffect(() => {
    if (!news.length || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const newsId = Number(entry.target.getAttribute("data-news-id"));
          const item = news.find((candidate) => Number(candidate.id) === newsId);
          if (item) recordNewsEvent(apiUrl, "news_view", item, `news_view:${item.id}`);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.5 }
    );

    const cards = document.querySelectorAll(".news-public-card[data-news-id]");
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [apiUrl, news]);

  return (
    <div className="news-public-page">
      <header className="news-public-heading">
        <div>
          <h1>Berita Kakao</h1>
          <p>Informasi terbaru seputar kakao, petani, kebun, dan kegiatan Sahabat Kakao.</p>
        </div>
        {!loading && !error && <span>{news.length} Berita</span>}
      </header>

      {loading ? (
        <div className="news-public-state">Memuat berita kakao...</div>
      ) : error ? (
        <div className="news-public-state error">{error}</div>
      ) : news.length === 0 ? (
        <div className="news-public-state">Belum ada berita yang dipublikasikan.</div>
      ) : (
        <div className="news-public-grid">
          {news.map((item) => (
            <article className="news-public-card" key={item.id} data-news-id={item.id}>
              <div className="news-public-thumbnail">
                <img
                  src={resolveThumbnailUrl(apiUrl, item.thumbnail_url)}
                  alt={`Thumbnail ${item.title}`}
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = fallbackThumbnail;
                  }}
                />
              </div>
              <div className="news-public-content">
                <div className="news-public-date">{formatDate(item.created_at)}</div>
                <h2>{item.title}</h2>
                <div className="news-public-tags">{item.hashtags}</div>
                <p>
                  {expandedNews.has(item.id) || item.description.length <= DESCRIPTION_PREVIEW_LENGTH
                    ? item.description
                    : `${item.description.slice(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd()}...`}
                </p>
                {item.description.length > DESCRIPTION_PREVIEW_LENGTH && (
                  <button
                    type="button"
                    className="news-public-more"
                    onClick={() => toggleDescription(item.id)}
                    aria-expanded={expandedNews.has(item.id)}
                  >
                    {expandedNews.has(item.id) ? "Tampilkan Lebih Sedikit" : "Lihat Selengkapnya..."}
                  </button>
                )}

                {(item.pdf_url || item.photo_url || item.video_url) && (
                  <div className="news-public-media">
                    {item.pdf_url && (
                      <button
                        type="button"
                        onClick={() =>
                          openMediaAccess(item, "pdf_click", item.pdf_url, "Lihat / Download Panduan PDF")
                        }
                      >
                        <FaFilePdf aria-hidden="true" /> Lihat atau Download Panduan PDF <FaExternalLinkAlt aria-hidden="true" />
                      </button>
                    )}
                    {item.photo_url && (
                      <button
                        type="button"
                        onClick={() => openMediaAccess(item, "photo_click", item.photo_url, "Lihat Photo")}
                      >
                        <FaImage aria-hidden="true" /> Lihat Photo <FaExternalLinkAlt aria-hidden="true" />
                      </button>
                    )}
                    {item.video_url && (
                      <button
                        type="button"
                        onClick={() => openMediaAccess(item, "video_click", item.video_url, "Lihat Video")}
                      >
                        <FaVideo aria-hidden="true" /> Lihat Video <FaExternalLinkAlt aria-hidden="true" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {mediaAccess && (
        <div className="news-access-backdrop" onClick={closeMediaAccess}>
          <div className="news-access-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="news-access-head">
              <div>
                <h2>{mediaAccess.label}</h2>
                <p>{mediaAccess.item.title}</p>
              </div>
              <button type="button" onClick={closeMediaAccess} disabled={submittingMediaAccess} aria-label="Tutup">
                x
              </button>
            </div>
            <form onSubmit={submitMediaAccess}>
              <p className="news-access-note">
                Isi Data Singkat Untuk Melanjutkan
              </p>
              <label>
                <span>Nama</span>
                <input
                  value={mediaAccessForm.name}
                  onChange={(e) => setMediaAccessForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Nama lengkap"
                  maxLength="160"
                  autoFocus
                  required
                />
              </label>
              <label>
                <span>Institusi</span>
                <input
                  value={mediaAccessForm.institution}
                  onChange={(e) => setMediaAccessForm((prev) => ({ ...prev, institution: e.target.value }))}
                  placeholder="Nama institusi/organisasi"
                  maxLength="220"
                  required
                />
              </label>
              {mediaAccessError && <div className="news-access-error">{mediaAccessError}</div>}
              <div className="news-access-actions">
                <button type="button" className="secondary" onClick={closeMediaAccess} disabled={submittingMediaAccess}>
                  Batal
                </button>
                <button type="submit" className="primary" disabled={submittingMediaAccess}>
                  {submittingMediaAccess ? "Memproses..." : mediaAccess.label}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
